import * as fabric from 'office-ui-fabric-react';
import { FC, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import * as api from '../../../../../../api/generated';
import { KendoLoading } from '../../../../../../components';
import ListItemCreateButton from '../../../../../../components/custom/ListItemCreateButton';
import { ILocationItem } from '../../../../../../components/custom/LocationTree';
import { useConfirmation } from '../../../../../../components/dialogs';
import { IWindowComponentProps, useWindow } from '../../../../../../components/windows/WindowContext';
import * as enums from '../../../../../../enums';
import { locationStatus, locationType } from '../../../../../../enums';
import { default as LocationConfigInfoScreenLocationsCreateWindow } from './LocationConfigInfoScreenLocationsCreateWindow';
import LocationConfigInfoScreenLocationsEditWindow from './LocationConfigInfoScreenLocationsEditWindow';

interface LocationConfigInfoScreenLocationsWindowProps {
    infoScreenId: number;
}

export interface ILocationDisplayItem {
    id: number;
    locationId: number;
    direction: enums.InfoScreenLocationDisplayDirectionType;
    locationName: string | null;
    locationType: enums.LocationType;
}

const LocationConfigInfoScreenLocationsWindow: FC<IWindowComponentProps<LocationConfigInfoScreenLocationsWindowProps>> = (props) => {
    const {
        componentProps: { infoScreenId },
        onClose
    } = props;
    const [selectedLocations, setSelectedLocations] = useState<ILocationDisplayItem[] | null>(null);
    const [unSelectedLocations, setUnselectedLocations] = useState<ILocationItem[] | null>(null);

    const { confirmAsync } = useConfirmation();
    const { openWindow } = useWindow();

    const fetchData = async () => {
        try {
            const [allLocations, infoScreenDisplayLocations] = await Promise.all([
                api.administration.getLocations(null),
                api.administration.getInfoScreenDisplayLocations(infoScreenId)
            ]);

            const selectedLocations: ILocationDisplayItem[] = [];
            const unselectedLocations: ILocationItem[] = [];

            allLocations
                .filter((x) => x.locationType === locationType.Cabinet || x.locationType === locationType.Room)
                .forEach((x) => {
                    let wasLocationSelected = false;
                    infoScreenDisplayLocations &&
                        infoScreenDisplayLocations.forEach((y) => {
                            if (y.locationId === x.id) {
                                selectedLocations.push({
                                    id: y.id,
                                    locationId: y.locationId,
                                    direction: y.direction,
                                    locationName: x.name,
                                    locationType: x.locationType
                                });
                                wasLocationSelected = true;
                            }
                        });
                    !wasLocationSelected && unselectedLocations.push(x);
                });

            setSelectedLocations(selectedLocations);
            setUnselectedLocations(unselectedLocations);
        } catch (ex) {
            toast.error(ex.error[0]);
        }
    };

    const handleApprove = async (id: number) => {
        try {
            const { success } = await confirmAsync('Įrašo ištrynimas', 'Ar tikrai norite nebevaizduoti lokacijos info ekrane?');

            if (!success) {
                return;
            }
            await api.administration.deleteInfoScreenDisplayLocation(id);
            toast.success('Lokacija sėkmingai atskirta nuo info ekrano vaizdavimo');
            fetchData();
        } catch (ex) {
            toast.error('Lokacijos nuo info ekrano atskirti nepavyko');
        }
    };

    const _allItemsColumns = [
        {
            key: 'column0',
            name: 'Veiksmai',
            minWidth: 80,
            maxWidth: 100,
            isResizable: true,
            onRender: (item: ILocationDisplayItem) => {
                return (
                    <>
                        <fabric.IconButton
                            onClick={async () => {
                                openLocationConfigInfoScreenLocationsEditWindow(item.id);
                            }}
                            iconProps={{
                                iconName: 'PageEdit'
                            }}
                            styles={{
                                root: {
                                    margin: '0px, 2px',
                                    color: fabric.getTheme().palette.themeDarker
                                }
                            }}
                        />
                        <fabric.IconButton
                            onClick={async () => await handleApprove(item.id)}
                            iconProps={{
                                iconName: 'Trash'
                            }}
                            styles={{
                                root: {
                                    margin: '0px, 2px',
                                    color: fabric.getTheme().semanticColors.errorText
                                }
                            }}
                        />
                    </>
                );
            }
        },
        {
            key: 'column1',
            name: 'Lokacijos pavadinimas',
            fieldName: 'locationName',
            minWidth: 150,
            maxWidth: 250,
            isResizable: true
        },
        {
            key: 'column2',
            name: 'Lokacijos tipas',
            fieldName: 'locationType',
            minWidth: 150,
            maxWidth: 250,
            isResizable: true,
            onRender: (item: ILocationDisplayItem) => enums.locationType.format(item.locationType)
        },
        {
            key: 'column3',
            name: 'Rodyklė',
            fieldName: 'direction',
            minWidth: 150,
            maxWidth: 250,
            isResizable: true,
            onRender: (item: ILocationDisplayItem) => enums.infoScreenLocationDisplayDirectionType.format(item.direction)
        }
    ];

    useEffect(() => {
        fetchData();
    }, []);

    const unselectedLocationsAndCurrentLocation = (selectedItemId: number): ILocationItem[] => {
        let arrayToReturn: ILocationItem[] = [];
        if (unSelectedLocations) {
            arrayToReturn = unSelectedLocations?.map((x) => x);

            const selectedItem = selectedLocations?.find((x) => x.id === selectedItemId);
            if (selectedItem) {
                arrayToReturn.push({
                    id: selectedItem.locationId,
                    name: selectedLocations?.find((x) => x.locationId === selectedItem?.locationId)?.locationName ?? '',
                    partOfLocationId: null,
                    locationType: locationType.Building,
                    locationStatus: locationStatus.Waiting,
                    key: null,
                    doorControllerKey: null,
                    activeTicketPrintingTimeBeforeVisit: null,
                    activeTicketPrintingTimeAfterVisit: null,
                    isActiveTicketPrintingTimeBeforeVisitEnabled: null,
                    isActiveTicketPrintingTimeAfterVisitEnabled: null,
                    floor: null
                });
            }
        }

        return arrayToReturn;
    };

    const openLocationConfigInfoScreenLocationsCreateWindow = async () => {
        try {
            LocationConfigInfoScreenLocationsCreateWindow.defaultProps = {
                componentProps: {
                    fetchData: fetchData,
                    unselectedLocations: unSelectedLocations,
                    infoScreenId: infoScreenId
                }
            };
            openWindow({
                title: 'Lokacijos priskyrimas info ekranui',
                component: LocationConfigInfoScreenLocationsCreateWindow,
                isDialogBehaviour: true,
                width: 800,
                height: 400
            });
        } catch (ex) {
            toast.error(ex.error[0]);
        }
    };

    const openLocationConfigInfoScreenLocationsEditWindow = async (editableItemId: number) => {
        try {
            LocationConfigInfoScreenLocationsEditWindow.defaultProps = {
                componentProps: {
                    fetchData: fetchData,
                    unselectedLocations: unselectedLocationsAndCurrentLocation(editableItemId),
                    editableItemId: editableItemId
                }
            };
            openWindow({
                title: 'Lokacijos priskyrimas info ekranui',
                component: LocationConfigInfoScreenLocationsEditWindow,
                isDialogBehaviour: true,
                width: 800,
                height: 400
            });
        } catch (ex) {
            toast.error(ex.error[0]);
        }
    };

    return (
        <>
            <div className="ms-Grid">
                {unSelectedLocations && (
                    <>
                        <div className="ms-Grid-row" style={{ textAlign: 'center', marginBottom: '10px' }}>
                            <fabric.Text style={{ fontWeight: 'lighter' }} variant="xxLarge">
                                Info ekrane atvaizduojamos lokacijos
                            </fabric.Text>
                        </div>
                        <div className="ms-Grid-row">
                            <fabric.Stack horizontal horizontalAlign="space-between" style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <div>
                                    <fabric.Label>Nuoroda į įrenginio rodomą vaizdą</fabric.Label>
                                    <fabric.Link target="_blank" href={`${window.location.origin}/display/infoScreen/${infoScreenId}`}>
                                        <fabric.Icon style={{ fontSize: '30px' }} iconName="PageArrowRight" />
                                    </fabric.Link>
                                </div>
                                <div>
                                    <ListItemCreateButton
                                        openCreateModal={openLocationConfigInfoScreenLocationsCreateWindow}
                                        text={unSelectedLocations.length < 1 ? 'Visos lokacijos yra pasirinktos' : 'Pridėti'}
                                        disabled={unSelectedLocations.length < 1}
                                    />
                                </div>
                            </fabric.Stack>
                        </div>
                    </>
                )}
            </div>
            {!selectedLocations && <KendoLoading />}
            {selectedLocations && (
                <div className="ms-Grid-row">
                    <div className="ms-Grid-col ms-md12">
                        <fabric.DetailsList
                            items={selectedLocations}
                            columns={_allItemsColumns}
                            checkboxVisibility={fabric.CheckboxVisibility.hidden}
                            selectionMode={fabric.SelectionMode.none}
                            disableSelectionZone={true}
                            setKey={'2'}
                            enterModalSelectionOnTouch={true}
                            selectionPreservedOnEmptyClick={true}
                        />
                    </div>
                </div>
            )}
        </>
    );
};

export default LocationConfigInfoScreenLocationsWindow;
